/* ==========================================================================
   Product Deep Details: Specs, SmartScreen Bypass Guide & FAQ (Bilingual)
   ========================================================================== */

export interface SystemSpecItem {
  label: string;
  value: string;
  en: {
    label: string;
    value: string;
  };
}

export interface FAQItem {
  question: string;
  answer: string;
  en: {
    question: string;
    answer: string;
  };
}

export interface SmartScreenGuide {
  title: string;
  intro: string;
  steps: string[];
  en: {
    title: string;
    intro: string;
    steps: string[];
  };
}

export interface ProductDetailItem {
  appId: string;
  headline: string;
  launchUrl: string;
  landingUrl: string;
  specs: SystemSpecItem[];
  smartScreen?: SmartScreenGuide;
  faq: FAQItem[];
  en: {
    headline: string;
  };
}

export const PRODUCT_DETAILS: Record<string, ProductDetailItem> = {
  'mindmap': {
    appId: 'mindmap',
    headline: 'IdeaMap — Sơ đồ tư duy trực quan & Trợ lý AI riêng tư',
    launchUrl: 'https://ideamap.nimbokit.com',
    landingUrl: 'https://ideamap.nimbokit.com/about',
    en: {
      headline: 'IdeaMap — Visual Mind Mapping & Private AI Canvas'
    },
    specs: [
      {
        label: 'Thiết bị hỗ trợ',
        value: 'Dùng ngay trên trình duyệt web (Chrome, Edge, Safari...) hoặc cài làm app máy tính',
        en: {
          label: 'Platform Support',
          value: 'Runs in modern web browsers (Chrome, Edge, Safari...) or installable desktop app'
        }
      },
      {
        label: 'Dùng khi mất mạng',
        value: '100% Offline — Không có internet vẫn vẽ, mở file và làm việc bình thường',
        en: {
          label: 'Offline Access',
          value: '100% Offline — Mind map and create smoothly with zero internet'
        }
      },
      {
        label: 'Nơi lưu dữ liệu',
        value: 'Lưu an toàn trên máy tính của bạn. Tùy chọn đồng bộ lên Google Drive cá nhân',
        en: {
          label: 'Data Storage',
          value: 'Stored safely on your computer. Optional sync to your personal Google Drive'
        }
      },
      {
        label: 'Mở rộng AI',
        value: 'Tùy ý kết nối trợ lý AI miễn phí (Google Gemini, Groq, OpenAI...) theo nhu cầu',
        en: {
          label: 'AI Expansion',
          value: 'Connect free AI assistants (Google Gemini, Groq, OpenAI...) with your own key'
        }
      },
      {
        label: 'Giá cả & Bản quyền',
        value: 'Dùng miễn phí trọn đời — Hoàn toàn không thu phí thuê bao hàng tháng',
        en: {
          label: 'Pricing & License',
          value: 'Free lifetime access — Zero recurring monthly subscription fees'
        }
      }
    ],
    faq: [
      {
        question: 'Dữ liệu sơ đồ tư duy của tôi có an toàn không?',
        answer: '100% dữ liệu nằm trên máy tính của bạn. Nimbokit không lưu giữ thông tin hay đọc sơ đồ của bạn. Khi bạn kết nối Google Drive, dữ liệu đi thẳng vào Drive cá nhân của bạn.',
        en: {
          question: 'Is my mind map data secure and private?',
          answer: '100% of your data stays on your personal computer. Nimbokit has no servers storing your maps. If you sync with Google Drive, files go directly into your personal Drive.'
        }
      },
      {
        question: 'Dùng trợ lý AI có tốn tiền không?',
        answer: 'Không. Bạn có thể dùng API Google Gemini hoàn toàn miễn phí hoặc Groq với hạn mức rất lớn. Bạn chỉ kết nối chìa khóa (API key) cá nhân nên không lo bị tính phí trung gian.',
        en: {
          question: 'Are there hidden fees for using the AI assistant?',
          answer: 'No. You can connect free API keys like Google Gemini or Groq directly with zero markup or hidden middleman fees.'
        }
      },
      {
        question: 'Tôi có thể xuất ra ảnh hoặc tài liệu không?',
        answer: 'Có. Bạn có thể xuất sơ đồ ra ảnh PNG sắc nét, file dữ liệu JSON để gửi cho đồng nghiệp, hoặc bản tóm tắt văn bản Markdown chỉ với 1 click.',
        en: {
          question: 'Can I export images and documents?',
          answer: 'Yes. You can export crisp high-res PNG images, JSON backup files to share, or Markdown text summaries with 1 click.'
        }
      }
    ]
  },

  'multi-tasks-autoclick': {
    appId: 'multi-tasks-autoclick',
    headline: 'Multi-Task AutoClick — Tự động hóa chuột & bàn phím Windows',
    launchUrl: 'https://multitasksautoclick.nimbokit.com/downloads/MultiTaskAutoClick_Setup.exe',
    landingUrl: 'https://multitasksautoclick.nimbokit.com/',
    en: {
      headline: 'Multi-Task AutoClick — Multi-Task Mouse & Keyboard Automation'
    },
    specs: [
      {
        label: 'Hệ điều hành',
        value: 'Windows 10 và Windows 11 (64-bit)',
        en: {
          label: 'Operating System',
          value: 'Windows 10 and Windows 11 (64-bit)'
        }
      },
      {
        label: 'Tốc độ & Hiệu năng',
        value: 'Xử lý đa luồng siêu tốc, click và gõ phím tức thì không làm đơ máy',
        en: {
          label: 'Speed & Performance',
          value: 'Ultra-fast multi-threaded processing, instant click and keystroke execution'
        }
      },
      {
        label: 'Kết nối mạng',
        value: '100% Offline — Không cần internet sau khi cài đặt, an toàn tuyệt đối',
        en: {
          label: 'Network Connection',
          value: '100% Offline — Zero internet required after setup, completely secure'
        }
      },
      {
        label: 'Cử động tự nhiên',
        value: 'Mô phỏng thao tác tay người thật với độ trễ ngẫu nhiên tránh bị phát hiện spam',
        en: {
          label: 'Human Cadence',
          value: 'Natural human motion with randomized delay intervals and gentle jitter'
        }
      },
      {
        label: 'Lưu quy trình',
        value: 'Lưu và nạp kịch bản làm việc (.json) dễ dàng chỉ trong 1 giây',
        en: {
          label: 'Workflow Presets',
          value: 'Save and replay routine scripts (.json) instantly with 1 click'
        }
      }
    ],
    smartScreen: {
      title: 'Hướng dẫn cài đặt nhanh (Vượt thông báo Windows bảo vệ)',
      intro: 'Do đây là phần mềm mới xuất bản, Windows Defender có thể hiện thông báo bảo vệ màu xanh trong lần đầu mở.',
      steps: [
        'Khi cửa sổ thông báo xuất hiện, bấm vào dòng chữ gạch chân: More info (Thông tin khác).',
        'Bấm vào nút Run anyway (Vẫn chạy) ở góc dưới bên phải.',
        'Phần mềm 100% an toàn, không chứa mã độc và bắt đầu cài đặt ngay.'
      ],
      en: {
        title: 'Quick Installation Guide (Windows Defender SmartScreen)',
        intro: 'As an independently crafted utility, Windows Defender may display a protective blue alert on first launch.',
        steps: [
          'When the alert appears, click the underlined link: More info.',
          'Click the Run anyway button at the bottom-right corner.',
          'The software is 100% virus-free, secure, and installs immediately.'
        ]
      }
    },
    faq: [
      {
        question: 'Làm sao để dừng click khẩn cấp nếu cài đặt nhầm?',
        answer: 'Phần mềm có phím tắt dừng khẩn cấp (Emergency Stop như phím Esc hoặc F12). Chỉ cần bấm phím là toàn bộ thao tác click sẽ dừng lại ngay lập tức.',
        en: {
          question: 'How do I emergency-stop if I set wrong coordinates?',
          answer: 'The software includes an instant Emergency Stop Hotkey (Esc or F12) that immediately halts all click routines.'
        }
      },
      {
        question: 'Chế độ cử động như người thật giúp ích gì?',
        answer: 'Thay vì click đều như máy móc dễ bị nhận diện tự động, phần mềm tự tạo độ trễ ngẫu nhiên tự nhiên như tay người, giúp thao tác an toàn và không bị chặn.',
        en: {
          question: 'How does natural human motion help?',
          answer: 'Instead of robotic rhythmic clicks that trigger anti-bot detection, randomized intervals mimic real hand movements smoothly and safely.'
        }
      },
      {
        question: 'Tôi có thể chia sẻ kịch bản cho đồng nghiệp không?',
        answer: 'Có. Mỗi kịch bản click là một file nhẹ, bạn có thể gửi file qua Zalo, Discord hoặc email để đồng nghiệp mở lên dùng ngay.',
        en: {
          question: 'Can I share scripts with my teammates?',
          answer: 'Yes. Each workflow is saved as a lightweight file that you can easily send to teammates to run instantly.'
        }
      }
    ]
  }
};
